import React, { useState } from 'react';
import { Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from '@mui/material';
import PostComposer from './PostComposer';
import { useNotification } from './NotificationContext';

interface ShareDivePromptProps {
  /** The dive that was just logged; null when there is nothing to offer. */
  diveId: string | null;
  /** Override the heading, e.g. for the single nudge after a bulk import. */
  title?: string;
  onClose: () => void;
}

/**
 * Shown right after a dive is logged: offers to post it to the feed. Accepting opens the
 * normal composer with the dive attached (audience defaults to Dive Buddies only, so
 * nothing goes public unless the diver picks that).
 */
const ShareDivePrompt: React.FC<ShareDivePromptProps> = ({ diveId, title = 'Dive logged! Share it with your buddies?', onClose }) => {
  const { showSuccess } = useNotification();
  const [composing, setComposing] = useState(false);

  const close = () => {
    setComposing(false);
    onClose();
  };

  if (!diveId) return null;

  return (
    <>
      <Dialog open={!composing} onClose={close}>
        <DialogTitle>{title}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Post this dive to your feed so your dive buddies can see it. You choose who can see it — it
            defaults to dive buddies only.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={close}>Not now</Button>
          <Button variant="contained" onClick={() => setComposing(true)}>Share dive</Button>
        </DialogActions>
      </Dialog>
      {composing && (
        <PostComposer
          open
          initialDiveId={diveId}
          onClose={close}
          onCreated={() => showSuccess('Posted to your feed!')}
        />
      )}
    </>
  );
};

export default ShareDivePrompt;
